<template>
    <div class="highlight" :class="{ 'left-align': data.alignment === 'left' }">
        <a-label v-if="data.is_labeled && label" v-bind="label">
            {{ label.text }}
        </a-label>
        <a-dangerous-html :content="text" />
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import ADangerousHtml from '@core/components/dangerous-html/dangerous-html.vue';
import ALabel from '@core/components/label/label.vue';
import type { BodyVariant, HighlightToolData } from '@model/editor-js/blocks';

defineOptions({
    name: 'BlockHighlight',
});

const props = defineProps<{
    id?: string;
    data: HighlightToolData;
    variant: BodyVariant;
}>();

const MARKDOWN_LINK = /\[(.*?)]\((https?:\/\/\S+)\)/gi;

// the CMS stores "no glyph" as null; a-label expects a string or nothing
const label = computed(() => props.data.label && { ...props.data.label, glyph: props.data.label.glyph ?? undefined });

const text = computed(() => props.data.text.replaceAll(MARKDOWN_LINK, '<a href="$2" title="$1" target="_blank">$1</a>'));
</script>

<style lang="pcss" scoped>
.body-renderer .highlight {
    position: relative;
    @mixin paragraph-accent;
    margin: 16px 0;
    padding: 16px;
    text-align: center;
    border-radius: 4px;
    color: var(--av-fixed-primary);
    background: var(--av-brand-accent);

    @media (--viewport-mobile-wide) {
        margin: 32px;
        padding: 32px;
        @mixin lead;
    }

    &.left-align {
        text-align: start;
    }

    :deep(.a-label) {
        position: absolute;
        top: -8px;
        inset-inline-start: 50%;
        transform: translateX(-50%);

        &.size-l {
            top: -12px;
        }
    }
}

.variant-tru .highlight {
    margin: 24px 0;
    color: inherit;
    background: var(--av-solid-brand-accent);

    @media (--viewport-mobile-wide) {
        margin: 24px 32px;
        padding: 32px;
        font-size: 18px;
        line-height: 32px;
    }
}
</style>
